(ns minorodata.components.dependency-graph.dag
  "xyflow canvas for the dependency-graph page. Node cards, cluster boxes and the
   zoom-out collapse all come from `minorodata.components.pipeline-dag.nodes`, so
   an entity looks identical here and in the pipeline editor; this ns wires the
   global page's own interactivity — clicking focuses a node, double-click opens
   the anchored subpipeline tab — and the monitoring toolbar (visibility toggles,
   group/tag filters, auto-refresh, minimap) that belongs to a global live view."
  (:require
   ["lucide-react" :refer [Check ChevronDown Folder Loader2
                           PanelRightClose PanelRightOpen RefreshCw Search
                           Settings Shrink Tags X]]
   ["react" :as react]
   [applied-science.js-interop :as j]
   [clojure.string :as str]
   [minorodata.app.handlers :refer [dispatch]]
   [minorodata.components.pipeline-dag.loading :as dag-loading]
   [minorodata.components.pipeline-dag.menu :as pd.menu]
   [minorodata.components.pipeline-dag.nodes :as pd.nodes]
   [minorodata.components.pipeline-editor.utils :refer [tab-icon]]
   [re-frame.core :as re-frame]
   [reagent.core :as reagent]
   [re-posh.core :as re-posh]
   [ui.button :as button]
   [ui.dag :as ui.dag]
   [ui.dropdown-menu :as dropdown-menu]
   [ui.fields :as fields]
   [ui.popover :as popover]))

(defn open-subpipeline [{:keys [id type title]}]
  (dispatch [:dependency-graph.events/open-anchored-tab
             {:anchor-id id :anchor-type type :label title}]))

;; Run a transform, optionally pulling in its upstream / downstream dependencies,
;; mirroring the bleene graph Run action: fetch the single transform first so its
;; current version is loaded, then run the latest version by master id. Inlined
;; (rather than required from `bleene.dag.context-menu`) to keep the
;; dependency-graph namespace free of cross-feature requires.
(defn- launch-transform! [transform-id group-id option master-id]
  (dispatch [:transform.api/fetch-single-transform
             {:transform-id  transform-id
              :group-id      group-id
              :add-to-panel? false
              :on-finish     #(dispatch [:transform.api/get-latest-version-by-master-id-and-run
                                         {:type             option
                                          :master-id        master-id
                                          :old-transform-id transform-id}])}]))

;; The node context menu mirrors the failures/recent-runs sidebar kebab (see
;; `minorodata.components.dependency-graph.sidebar`): the same cross-feature
;; actions (Kai, logs, edit, pipeline editor), so a node offers the same options
;; whether reached on the canvas or from the sidebar. "Focus on graph"/"reveal"
;; are dropped here — the node is already on the graph. The Kai item is
;; "Debug with KAI" for a failed transform, neutral "Ask KAI" otherwise.
(defn- focus! [id]
  (dispatch [:dependency-graph.events/focus-node id]))

;; top-level, for a stable identity in the canvas's Esc listener
(defn- unpin! []
  (dispatch [:dependency-graph.events/clear-focus]))

;; Node cards are presentational; right-click and hover are handled once at the canvas.

(defn- node-root
  "Click handlers and focus/highlight state shared by every node type."
  [data highlighted?]
  (let [{:keys [id]} data]
    {:on-click        #(focus! id)
     :on-double-click #(open-subpipeline data)
     :highlighted?    highlighted?
     :selected?       @(re-frame/subscribe [:dependency-graph.subs/node-focused? id])}))

(defn- entity-body
  "Card or, zoomed out, the collapsed box — kept outside `node-group`, which has no height of its own."
  [{:keys [data status last-run collapsed? highlighted?]}]
  (let [root (node-root data highlighted?)]
    (if collapsed?
      (pd.nodes/collapsed-card {:data         data
                                :status       status
                                :selected     (:selected? root)
                                :highlighted? highlighted?
                                :root         root})
      [ui.dag/node-group
       (pd.nodes/entity-card {:data     data
                              :status   status
                              :last-run last-run
                              :root     root})])))

(defn- transform-node' [{:keys [data collapsed?]}]
  (let [{:keys [id metadata]} data
        {:keys [status last-run]} @(re-posh/subscribe
                                    [:dependency-graph.subs/transform-status
                                     (:transform-id metadata)])
        ;; A node's id is its master id here (the same one `open-timeline` and
        ;; `focus-node` are given), so it matches an execution-log row directly.
        highlighted? @(re-frame/subscribe [:dependency-graph.subs/node-hovered? id])]
    (entity-body {:data data :status status :last-run last-run
                  :collapsed? collapsed? :highlighted? highlighted?})))

(defn- extract-node' [{:keys [data collapsed?]}]
  (let [{:keys [status last-run]} @(re-posh/subscribe
                                    [:statuses.subs/extract-status
                                     (:extract-id (:metadata data))])]
    (entity-body {:data data :status status :last-run last-run
                  :collapsed? collapsed?})))

(defn- analytics-model-node' [{:keys [data collapsed?]}]
  (let [{:keys [status last-run]} @(re-posh/subscribe
                                    [:statuses.subs/model-status
                                     (:model-id (:metadata data))])]
    (entity-body {:data data :status status :last-run last-run
                  :collapsed? collapsed?})))

;; Table nodes only appear when the "Tables" toggle is on (they are otherwise
;; contracted out — see `contract-tables`). A table has no execution status, so the
;; card carries no status bar. Double-click scopes down to the anchored subpipeline,
;; exactly as every other node type does — handing off to the pipeline editor is the
;; explicit "Pipeline editor" menu item, not an accident of node type.
;;
;; Tables render as the same compact PILL the editor uses, with the name in a
;; tooltip: a table is a waypoint between the transforms that matter here, and at a
;; full card's width a warehouse's worth of them is most of the graph — the more so
;; now that a table lays out INSIDE its producer's group box (see
;; `layout/assign-table-containers`), where a 400px card stretches the box it sits
;; in. The layout has to agree, hence `:table-shape :pill` in
;; `dependency-graph.events/layout-graph`.
;;
;; A pill draws no text; its name shows in the canvas-level `ui.dag/hover-tooltip`.
(defn- table-node' [{:keys [data collapsed?]}]
  (let [root (node-root data nil)]
    (if collapsed?
      ;; not wrapped in `node-group` — see `entity-body`
      (pd.nodes/collapsed-card {:data data :status nil :selected (:selected? root)
                                :shape :pill :root root})
      [ui.dag/node-group
       (pd.nodes/table-card {:data data :shape :pill :root root})])))


(def ^:private tooltip-cards
  {"transform" transform-node' "extract" extract-node' "model" analytics-model-node'})

(defn- node-tooltip
  "The full card for a hovered collapsed node; tables keep the name tooltip."
  [^js node]
  (when-let [card (tooltip-cards (j/get node :type))]
    [card {:data (js->clj (j/get node :data) :keywordize-keys true) :collapsed? false}]))

(def node-types
  (js/Object.assign
   #js {}
   #js {:transform (pd.nodes/entity-node transform-node')
        :extract   (pd.nodes/entity-node extract-node')
        :table     (pd.nodes/entity-node table-node')
        :model     (pd.nodes/entity-node analytics-model-node')}
   pd.nodes/container-node-types))


;; =============================================================================
;; Minimap coloring
;; =============================================================================

(defn- node-container-kind
  "The group/source container a minimap node belongs to: its own type when it IS
   a container box, otherwise its parent container's kind (parsed from `parentId`,
   e.g. \"source:42\"), or nil for loose nodes."
  [node]
  (let [type (j/get node :type)]
    (if (contains? pd.nodes/container-color-var type)
      type
      (when-let [pid (j/get node :parentId)]
        (first (str/split pid #":" 2))))))

(defn- minimap-node-color
  "Fill for a minimap block. Container boxes (group/source) get a faint tint so
   they read as a colored region; member nodes get their container's solid color.
   Loose nodes (no group/source) stay neutral."
  [node]
  (let [color (get pd.nodes/container-color-var (node-container-kind node))]
    (cond
      (nil? color) "var(--color-slate-300)"
      (contains? pd.nodes/container-color-var (j/get node :type))
      (str "color-mix(in oklch, " color " 12%, transparent)")
      :else        "transparent")))

(defn- page-settings [{:keys [show-sources? show-tables? show-groups? compact?]}]
  [dropdown-menu/dropdown-menu-root
   [dropdown-menu/dropdown-menu-trigger
    [button/button {:component :div
                    :icon      Settings
                    :variant   :empty-transparent
                    :size      :icon
                    :icon-props {:size 24}
                    :title     "Settings"}]]
   [dropdown-menu/dropdown-menu-content
    [dropdown-menu/dropdown-menu-label "Show"]
    [dropdown-menu/dropdown-menu-checkbox
     {:icon              Check
      :checked           show-sources?
      :on-select         #(.preventDefault %)
      :on-checked-change #(dispatch [:dependency-graph.events/toggle-sources])}
     [:span {:class "flex items-center gap-2"}
      [tab-icon {:type "extract"}] "Extracts"]]
    [dropdown-menu/dropdown-menu-checkbox
     {:icon              Check
      :checked           show-tables?
      :on-select         #(.preventDefault %)
      :on-checked-change #(dispatch [:dependency-graph.events/toggle-tables])}
     [:span {:class "flex items-center gap-2"}
      [tab-icon {:type "table"}] "Tables"]]
    [dropdown-menu/dropdown-menu-checkbox
     {:icon              Check
      :checked           show-groups?
      :on-select         #(.preventDefault %)
      :on-checked-change #(dispatch [:dependency-graph.events/toggle-groups])}
     [:span {:class "flex items-center gap-2"}
      [:> Folder {:size 16}] "Groups"]]
    [dropdown-menu/dropdown-menu-label "View"]
    [dropdown-menu/dropdown-menu-checkbox
     {:icon              Check
      :checked           compact?
      :on-select         #(.preventDefault %)
      :on-checked-change #(dispatch [:dependency-graph.events/toggle-compact])}
     [:span {:class "flex items-center gap-2" :title "Narrower cards, tighter spacing"}
      [:> Shrink {:size 16}] "Compact"]]]])

(def ^:private auto-refresh-options
  "Auto-refresh interval choices. `nil` value = off. Values are ms, stringified
   for the (string-only) Radix radio group and parsed back on change."
  [{:label "Off"        :value nil}
   {:label "10 seconds" :value 10000}
   {:label "1 minute"   :value 60000}
   {:label "5 minutes"  :value 300000}
   {:label "15 minutes" :value 900000}])

(defn- refresh-control
  "Refresh transform + extract statuses now (left button), grouped with a chevron
   dropdown that sets an auto-refresh interval. The chevron goes `:secondary` while
   auto-refresh is on so it reads as enabled."
  []
  (let [auto-refresh-ms @(re-posh/subscribe [:dependency-graph.subs/auto-refresh-ms])
        ;; same flag the canvas banner reads, so the two can't disagree
        loading?        @(re-posh/subscribe [:dependency-graph.subs/statuses-loading?])]
    [:span {:class "inline-flex items-center"}
     [button/button {:icon       RefreshCw
                     :variant    :empty-transparent
                     :size       :icon
                     :icon-props {:size 24 :class (when loading? "animate-spin")}
                     :title      (if auto-refresh-ms
                                   (str "Refresh statuses (auto every "
                                        (:label (some #(when (= auto-refresh-ms (:value %)) %) auto-refresh-options))
                                        ")")
                                   "Refresh statuses")
                     :class      "rounded-r-none"
                     :on-click   #(dispatch [:dependency-graph.events/refresh-statuses])}]
     [dropdown-menu/dropdown-menu-root
      [dropdown-menu/dropdown-menu-trigger
       [button/button {:component  :div
                       :icon       ChevronDown
                       :variant    (if auto-refresh-ms :empty :empty-transparent)
                       :class      "pl-0 pr-0.5 rounded-l-none "
                       :icon-props {:size 16}
                       :title      "Auto refresh"}]]
      [dropdown-menu/dropdown-menu-content {:align "end"}
       [dropdown-menu/dropdown-menu-label "Refresh every"]
       ;; Radix radio groups are string-keyed; nil (off) round-trips as "".
       [dropdown-menu/dropdown-menu-radio-group
        {:value           (str auto-refresh-ms)
         :on-value-change #(dispatch [:dependency-graph.events/set-auto-refresh
                                      (when (seq %) (js/parseInt %))])}
        (for [{:keys [label value]} auto-refresh-options]
          ^{:key (str value)}
          [dropdown-menu/dropdown-menu-radio-item {:value (str value)}
           label])]]]]))

;; =============================================================================
;; Group / tag filter toolbar
;; =============================================================================
;;
;; Selecting a group/tag DROPS the non-matching nodes and re-runs ELK (see
;; `filter-entities` / `layout-graph` in the events ns) — the graph re-packs
;; around the survivors. The selectable options come from
;; `:dependency-graph.subs/filter-options`, which derives them from the RAW
;; (unfiltered) graph so no option disappears once a filter is applied.

(def ^:private option-row-class
  "flex items-center gap-2 rounded-xs px-2 py-1.5 text-sm cursor-pointer hover:bg-kleene-hover-background")

(defn- popover-search-field
  "The search box heading a toolbar popover; `query` is the caller's atom."
  [{:keys [placeholder query on-key-down]}]
  [:div {:class "p-2 border-b border-kleene-layout-border"}
   [fields/input {:placeholder placeholder
                  :icon        Search
                  :auto-focus  true
                  :full-width  true
                  :class       "min-w-0"
                  :value       @query
                  :on-change   #(reset! query (j/get-in % [:target :value]))
                  :on-key-down on-key-down}]])

(defn- combobox-option
  "One selectable checkbox row inside the combobox popover."
  [{:keys [value label selected? on-toggle]}]
  [:div {:class    option-row-class
         :on-click #(on-toggle value)}
   [:span {:class "inline-flex items-center justify-center w-4 h-4 shrink-0"}
    (when selected? [:> Check {:size 14}])]
   [:span {:class "truncate"} label]])

(defn- filter-combobox
  "Compact toolbar combobox: a trigger button showing the active-selection count,
   opening a popover with a search field and a multi-select checkbox list.
   Clicking a row toggles it and leaves the popover open so several can be picked.

   Options are given either flat via `:options` (`[{:value :label}]`) or split into
   headed groups via `:sections` (`[{:heading <label> :options [...]}]`). `selected`
   is the set of chosen values; `on-toggle` is called with a value to flip it. The
   search box filters options within each section; sections left empty (and thus any
   empty group heading) aren't rendered.

   A form-2 component so each instance keeps its own popover-open / search-query
   state (reset when the popover closes)."
  [_]
  (let [open?  (reagent/atom false)
        query  (reagent/atom "")]
    (fn [{:keys [icon label options sections selected on-toggle]}]
      (let [selected (set selected)
            q        (str/lower-case (str/trim @query))
            ;; normalise flat options to a single unlabelled section, then drop any
            ;; section with no options left after filtering by the search query
            visible  (->> (or sections [{:options options}])
                          (keep (fn [section]
                                  (let [opts (cond->> (:options section)
                                               (seq q) (filterv #(str/includes? (str/lower-case (str (:label %))) q)))]
                                    (when (seq opts) (assoc section :options opts))))))]
        [popover/popover-root {:open           @open?
                               :on-open-change (fn [o]
                                                 (reset! open? o)
                                                 (when-not o (reset! query "")))}
         ;; asChild target is a native element (not the `button` fn component) so
         ;; Radix can forward the ref it anchors the popover to — matches the
         ;; trigger idiom in `ui.fields/searchable-select` / `ui.autocomplete`.
         [popover/popover-trigger {:as-child true}
          [:div {:class "inline-flex"}
           [button/button {:component        :div
                           :variant          (if (seq selected) :secondary :empty-transparent)
                           :size             :small
                           :icon             icon
                           :icon-props       {:size 16}
                           :icon-right       ChevronDown
                           :icon-right-props {:size 14}}
            (if (seq selected) (str label " (" (count selected) ")") label)]]]
         [popover/popover-content {:align "start"
                                   :class "p-0 w-70"}
          [popover-search-field {:placeholder (str "Search " (str/lower-case label) "…")
                                 :query       query}]
          [:div {:class "max-h-64 overflow-y-auto"}
           (if (empty? visible)
             [:div {:class "px-2 py-4 text-sm text-center text-kleene-text-secondary"}
              "No matches"]
             (for [{:keys [heading options]} visible]
               ^{:key (or heading "")}
               [:div
                (when heading
                  [:div {:class "px-2 pt-1.5 pb-0.5 text-xs font-medium text-kleene-text-secondary bg-kleene-background sticky top-0"}
                   heading])
                [:div {:class "px-1"}
                 (for [{:keys [value label]} options]
                   ^{:key value}
                   [combobox-option {:value     value
                                     :label     label
                                     :selected? (contains? selected value)
                                     :on-toggle on-toggle}])]]))]]]))))

(defn statuses-banner
  "Canvas notice while the per-node run statuses are still being fetched.

   The graph does not wait for them, so until they land a card reads \"Never
   executed\" — indistinguishable from an entity that really has never run."
  []
  (when @(re-posh/subscribe [:dependency-graph.subs/statuses-loading?])
    [:div {:class   "flex items-center gap-2 py-1.5 px-2.5 bg-kleene-background rounded-lg shadow-md border text-xs text-kleene-text-secondary"
           :data-cy "dag-statuses-loading"}
     [:> Loader2 {:size 14 :class "text-kleene-primary-v2 animate-spin shrink-0"}]
     ;; Kinds unnamed: models are only fetched behind `fe.analytics-models`, so
     ;; listing them would promise a fetch that isn't running for every client.
     "Loading run statuses\u2026"]))

(defn- relayout-banner-inner []
  ;; delayed, so a cached layout landing in a few ms doesn't flash it
  (let [[shown? set-shown] (react/useState false)]
    (react/useEffect
     (fn []
       (let [t (js/setTimeout #(set-shown true) 300)]
         #(js/clearTimeout t)))
     #js [])
    (when shown?
      [:div {:class   "flex items-center gap-2 py-1.5 px-2.5 bg-kleene-background rounded-lg shadow-md border text-xs text-kleene-text-secondary"
             :data-cy "dag-relayouting"}
       [:> Loader2 {:size 14 :class "text-kleene-primary-v2 animate-spin shrink-0"}]
       "Updating layout\u2026"])))

(def ^:private max-search-results 50)

(def ^:private searchable-types #{"transform" "extract" "model" "table"})

(defn search-matches
  "Nodes whose title contains `query`, prefix matches first."
  [nodes query]
  (let [q (str/lower-case (str/trim query))]
    (when (seq q)
      (->> nodes
           (keep (fn [{:keys [id type data]}]
                   (let [title (str (:title data))
                         t     (str/lower-case title)]
                     (when (and (searchable-types type) (str/includes? t q))
                       {:id id :type type :title title :t t :prefix? (str/starts-with? t q)}))))
           (sort-by (juxt (complement :prefix?) :t))
           (take max-search-results)))))

(defn- node-search
  "Find a drawn node by name; picking one focuses it (centres it, pins its lineage)."
  [_]
  (let [open? (reagent/atom false)
        query (reagent/atom "")]
    (fn [{:keys [nodes]}]
      (let [hits (search-matches nodes @query)
            pick (fn [id]
                   (focus! id)
                   (reset! open? false)
                   (reset! query ""))]
        [popover/popover-root {:open           @open?
                               :on-open-change (fn [o]
                                                 (reset! open? o)
                                                 (when-not o (reset! query "")))}
         [popover/popover-trigger {:as-child true}
          [:div {:class "inline-flex"}
           [button/button {:component  :div
                           :variant    :empty-transparent
                           :size       :small
                           :icon       Search
                           :icon-props {:size 16}}
            "Find"]]]
         [popover/popover-content {:align "start"
                                   :class "p-0 w-80"}
          [popover-search-field {:placeholder "Find a transform, extract or table…"
                                 :query       query
                                 :on-key-down #(when (and (= "Enter" (j/get % :key)) (seq hits))
                                                 (pick (:id (first hits))))}]
          [:div {:class "max-h-72 overflow-y-auto px-1 py-1"}
           (cond
             (str/blank? @query)
             [:div {:class "px-2 py-3 text-sm text-center text-kleene-text-secondary"}
              "Type to search this graph"]

             (empty? hits)
             [:div {:class "px-2 py-3 text-sm text-center text-kleene-text-secondary"}
              "No matches on this graph"]

             :else
             (for [{:keys [id type title]} hits]
               ^{:key id}
               [:div {:class    option-row-class
                      :on-click #(pick id)}
                [tab-icon {:type type :size 14}]
                [:span {:class "truncate"} title]]))]]]))))

(defn filter-bar
  "Top-left canvas toolbar: node search, then multi-select group and tag
   comboboxes that filter the graph (drop non-matching nodes + relayout). Filter
   options come from the raw-graph-derived `filter-options` sub, so they stay
   stable as the visible graph shrinks."
  [{:keys [tab-id nodes]}]
  (let [{:keys [group-ids tags]}        @(re-posh/subscribe [:dependency-graph.subs/filters])
        {:keys [group-sections] tag-list :tags} @(re-posh/subscribe [:dependency-graph.subs/filter-options tab-id])
        tag-options (mapv (fn [tag] {:value tag :label tag}) tag-list)]
    [:div {:class "flex items-center gap-1 p-1 bg-kleene-background rounded-lg shadow-md border"}
     [node-search {:nodes nodes}]
     (when (seq group-sections)
       [filter-combobox {:icon      Folder
                         :label     "Group"
                         :sections  group-sections
                         :selected  group-ids
                         :on-toggle #(dispatch [:dependency-graph.events/toggle-filter-group %])}])
     (when (seq tag-options)
       [filter-combobox {:icon      Tags
                         :label     "Tags"
                         :options   tag-options
                         :selected  tags
                         :on-toggle #(dispatch [:dependency-graph.events/toggle-filter-tag %])}])
     (when (or (seq group-ids) (seq tags))
       [button/button {:variant    :empty-transparent
                       :size       :icon
                       :icon       X
                       :icon-props {:size 16}
                       :title      "Clear filters"
                       :on-click   #(dispatch [:dependency-graph.events/clear-filters])}])]))

;; =============================================================================
;; Single canvas context menu
;; =============================================================================
;;
;; One Radix menu for the whole canvas, opened at the cursor from React Flow's
;; `on-node-context-menu`, instead of a Radix ContextMenu per node. Items are
;; chosen from the right-clicked node's type; permissions are resolved once here
;; rather than in every node card. `*-menu-items` return a vector of items spliced
;; into the content. Actions mirror the failures/recent-runs sidebar kebab.

(defn- transform-menu-items
  [{:keys [id title group-id metadata] :as node} {:keys [can-write? can-read-logs?]}]
  (let [transform-id (:transform-id metadata)
        failure?     (= "failed" (:status @(re-posh/subscribe
                                           [:dependency-graph.subs/transform-status transform-id])))
        timeline?    @(re-posh/subscribe [:unleash/is-enabled "fe.execution-timeline"])]
    [(pd.menu/section "Actions")
     (pd.menu/run-item {:can-write? can-write?
                        :on-run     #(launch-transform! transform-id group-id % id)})
     (pd.menu/open-tab-item {:on-click #(open-subpipeline node)})
     (pd.menu/kai-item {:master-id id :group-id group-id :title title :failure? failure?})
     (pd.menu/section "Preview")
     (pd.menu/logs-item
      {:disabled? (not can-read-logs?)
       :on-click  #(dispatch [:dependency-graph.events/open-logs
                              {:name title :filter {:term transform-id}}])})
     (when timeline?
       (pd.menu/timeline-item
        {:on-click #(dispatch [:dependency-graph.events/open-timeline {:master-id id :name title :source "graph"}])}))
     (pd.menu/section "Open in")
     (pd.menu/settings-item
      {:on-click #(dispatch [:app.events/navigate :transform-details
                             {:group-id group-id :transform-id transform-id}])})
     (pd.menu/sql-console-item {:transform-id transform-id :can-write? can-write?})
     (pd.menu/pipeline-editor-item {:id id :group-id group-id :type "transform" :name title})]))

(defn- extract-menu-items
  [{:keys [title group-id metadata] :as node} {:keys [can-read-logs?]}]
  (let [source-id  group-id ;; `:group-id` carries the source-id for extracts (see `enrich-node`)
        extract-id (:extract-id metadata)
        task-def   (:task-definition-name metadata)]
    [(pd.menu/section "Actions")
     (pd.menu/open-tab-item {:on-click #(open-subpipeline node)})
     (pd.menu/section "Preview")
     (pd.menu/logs-item
      {:disabled? (not can-read-logs?)
       :on-click  #(dispatch [:dependency-graph.events/open-logs
                              {:name   title
                               :filter (if (not-empty task-def)
                                         {:source (str/replace task-def #"-extract-dev|-extract-production" "")}
                                         {:term title})}])})
     (pd.menu/section "Open in")
     (pd.menu/settings-item
      {:on-click #(dispatch [:app.events/navigate :extract-details
                             {:source-id source-id :extract-id extract-id}])})
     (pd.menu/pipeline-editor-item {:id extract-id :group-id source-id :type "extract" :name title})]))

(defn- model-menu-items [{:keys [id] :as node}]
  [(pd.menu/section "Actions")
   (pd.menu/open-tab-item {:on-click #(open-subpipeline node)})
   (pd.menu/section "Open in")
   (pd.menu/settings-item
    {:on-click #(dispatch [:app.events/navigate :analytics-model-instance-details
                           {:instance-id id}])})])

(defn- table-menu-items [{:keys [id title group-id] :as node}]
  [(pd.menu/section "Actions")
   (pd.menu/open-tab-item {:on-click #(open-subpipeline node)})
   (pd.menu/section "Open in")
   (pd.menu/pipeline-editor-item {:id id :group-id group-id :type "table" :name title})])

(defn- node-menu-items
  "Menu items for the right-clicked node, dispatched on its type. `perms` carries the
   once-resolved `:can-write?` / `:can-read-logs?` from `pd.menu/graph-context-menu`."
  [{:keys [type] :as node} perms]
  (case type
    "transform" (transform-menu-items node perms)
    "extract"   (extract-menu-items node perms)
    "model"     (model-menu-items node)
    "table"     (table-menu-items node)
    []))

(defn subdag [_]
  ;; form-2: the right-click target (`{:node :x :y}`) is local state for this canvas,
  ;; driving the single `graph-context-menu`.
  (let [ctx-target (reagent/atom nil)
        token-cache (atom nil)]
    (fn [{:keys [dag-data loading? failed? relayouting?]}]
      (let [dark-mode?    @(re-posh/subscribe [:app/prop :app/dark-mode?])
            collapsed?    @(re-posh/subscribe [:dependency-graph.subs/failures-collapsed?])
            focus-node-id @(re-posh/subscribe [:dependency-graph.subs/focus-node-id])
            focus-tick    @(re-posh/subscribe [:dependency-graph.subs/focus-tick])
            show-sources? @(re-frame/subscribe [:dependency-graph.subs/show-sources?])
            show-tables?  @(re-frame/subscribe [:dependency-graph.subs/show-tables?])
            show-groups?  @(re-frame/subscribe [:dependency-graph.subs/show-groups?])
            compact?      @(re-frame/subscribe [:dependency-graph.subs/compact?])
            nodes         (:nodes dag-data)
            node-set-token (let [cached @token-cache]
                             (if (identical? nodes (:nodes cached))
                               (:token cached)
                               (let [t (hash (into #{} (map :id) nodes))]
                                 (reset! token-cache {:nodes nodes :token t})
                                 t)))]
        [:<>
         [ui.dag/dag {:nodes      nodes
                      :edges      (:edges dag-data)
                      :node-types node-types
                      :dark-mode? dark-mode?
                      :min-zoom   0.05
                      :hover-lineage? true
                      :pinned-node-id focus-node-id
                      :on-unpin       unpin!
                      ;; large graphs (100s–1000s of nodes): cull off-screen nodes from
                      ;; the DOM, and collapse to status boxes below the zoom threshold
                      ;; via one canvas-level zoom read (not a per-node subscription).
                      :only-render-visible? true
                      :collapse-below-zoom  pd.nodes/zoom-threshold
                      ;; one context menu for every node (see `pd.menu/graph-context-menu`).
                      ;; `on-context-menu` calls what it's given, so the atom has to be
                      ;; wrapped in a setter — a reagent atom isn't `IFn`.
                      :on-node-context-menu (pd.menu/on-context-menu #(reset! ctx-target %))}
          [ui.dag/background {:dark? dark-mode?}]
          [ui.dag/hover-tooltip {:render-node node-tooltip}]
          ;; centres the node a clicked timeline row (or a `?focus=` link) points at,
          ;; and refits when the filter or a visibility toggle re-shapes the graph
          [ui.dag/viewport-controller {:focus-node-id focus-node-id
                                       :focus-token   focus-tick
                                       :fit-token     node-set-token}]
          (when loading?
            [ui.dag/overlay {:position :top-left :class "w-full h-full flex items-center justify-center"}
             [dag-loading/loading-screen]])
          (when failed?
            [ui.dag/overlay {:position :top-left :class "w-full h-full flex items-center justify-center pointer-events-none"}
             [dag-loading/failed-screen]])
          (when-not loading?
            [ui.dag/overlay {:position :top-left :class "flex flex-col items-start gap-2"}
             [filter-bar {:tab-id (:id dag-data) :nodes nodes}]
             (when relayouting?
               [:f> relayout-banner-inner])
             [statuses-banner]])
          [ui.dag/overlay {:position :bottom-left :class "flex items-end gap-2"}
           [ui.dag/zoom-controls {:focus-node-id focus-node-id}]
           (when-not loading?
             [ui.dag/minimap {:node-color minimap-node-color}])]
          [ui.dag/overlay {:position :top-right :class "flex items-center gap-2 right-0"}
           ;; Graph actions in their own pill…
           (when-not loading?
             [:div {:class "p-0.5 bg-kleene-background rounded-lg shadow-md border"}
              [refresh-control]
              [page-settings {:show-sources? show-sources? :show-tables? show-tables? :show-groups? show-groups?
                              :compact? compact?}]])
           ;; …and the summary sidebar toggle separated into its own, squared off and
           ;; flush against the edge it opens — the mirror of the pipeline editor's
           ;; sidebar toggles (see `pipeline-editor.dag` / `pipeline-editor.panel`).
           [:div {:class "p-0.5 bg-kleene-background rounded-lg rounded-r-none shadow-md border"}
            [button/button {:icon       (if collapsed? PanelRightOpen PanelRightClose)
                            :variant    :empty-transparent
                            :size       :icon
                            :icon-props {:size 24}
                            :title      (if collapsed? "Show summary" "Hide summary")
                            :on-click   #(dispatch [:dependency-graph.events/toggle-failures])}]]]]
         [pd.menu/graph-context-menu {:target   @ctx-target
                                      :items    node-menu-items
                                      :on-close #(reset! ctx-target nil)}]]))))
